import React, { useState, useEffect } from 'react';
import {
  Card,
  Accordion,
  Row,
  Col,
  Button,
  Spinner,
  Alert,
} from 'react-bootstrap';

import { useOrderDetailContext } from 'providers/OrderDetailProvider';

import useFetchById from 'hooks/useFetchById';

import SubtleBadge from 'components/common/SubtleBadge';
import AddressEditModal from 'components/add_order/order/OrderDetail/AddressEditModal';

import { orderApi } from 'api';
import { getOrderBadgeColor } from 'constants/order';
import { TIME_OPTION } from 'constants/time';

const OrderDetailSummary = ({ orderId, listenToUpdates = false }) => {
  const { updateOrderDetailData } = useOrderDetailContext();

  const [activeKey, setActiveKey] = useState('0');
  const [isEditingBilling, setIsEditingBilling] = useState(false);
  const [isEditingShipping, setIsEditingShipping] = useState(false);

  const {
    data: orderData,
    loading,
    error,
    refetch,
  } = useFetchById(orderApi, orderId);

  useEffect(() => {
    if (listenToUpdates) {
      const handleUpdate = () => {
        refetch();
      };

      window.addEventListener('order-updated', handleUpdate);

      return () => window.removeEventListener('order-updated', handleUpdate);
    }
  }, [listenToUpdates, refetch]);

  useEffect(() => {
    if (orderData) {
      updateOrderDetailData(orderData);
    }
  }, [orderData, updateOrderDetailData]);

  if (loading) {
    return (
      <Card className="d-flex justify-content-center align-items-center p-4">
        <Spinner animation="border" variant="primary" />
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <Card.Body>
          <Alert variant="danger">{error}</Alert>
        </Card.Body>
      </Card>
    );
  }

  return (
    <>
      <Accordion activeKey={activeKey} onSelect={key => setActiveKey(key)}>
        <Card>
          {/* Order Details */}
          <Accordion.Item eventKey="0">
            <Accordion.Header className="d-flex justify-content-between">
              <span className="fw-semibold">Order Details</span>
            </Accordion.Header>
            <Accordion.Body className="p-0">
              <div className="p-3">
                <Row className="g-2">
                  <Col xs={6} className="text-muted fs-10">
                    Order No.
                  </Col>
                  <Col xs={6} className="text-end fw-medium fs-10">
                    {orderData?.channel_order_id}
                  </Col>

                  <Col xs={6} className="text-muted fs-10">
                    Order Date
                  </Col>
                  <Col xs={6} className="text-end fs-10">
                    {new Date(orderData?.channel_order_created).toLocaleString(
                      'en-IN',
                      TIME_OPTION,
                    )}
                  </Col>

                  <Col xs={6} className="text-muted fs-10">
                    Status
                  </Col>
                  <Col xs={6} className="text-end fs-10">
                    <SubtleBadge
                      bg={getOrderBadgeColor(orderData?.order_status)}
                    >
                      {orderData?.order_status_label}
                    </SubtleBadge>
                  </Col>

                  <Col xs={6} className="text-muted fs-10">
                    Customer GSTIN
                  </Col>
                  <Col xs={6} className="text-end fs-10">
                    {orderData?.gstin}
                  </Col>

                  <Col xs={6} className="text-muted fs-10">
                    Channel
                  </Col>
                  <Col xs={6} className="text-end fs-10">
                    {orderData?.channel?.channel_type}
                  </Col>

                  <Col xs={6} className="text-muted fs-10">
                    Payment Method
                  </Col>
                  <Col xs={6} className="text-end fs-10">
                    {orderData?.payment_type.toUpperCase()}
                  </Col>

                  <Col xs={6} className="text-muted fs-10">
                    Order Amount
                  </Col>
                  <Col xs={6} className="text-end fs-10">
                    ₹{orderData?.payment_order_amount}
                  </Col>

                  <Col xs={6} className="text-muted fs-10">
                    Channel Created
                  </Col>
                  <Col xs={6} className="text-end fs-10">
                    {new Date(orderData?.channel_order_created).toLocaleString(
                      'en-IN',
                      TIME_OPTION,
                    )}
                  </Col>

                  <Col xs={6} className="text-muted fs-10">
                    WMS Created
                  </Col>
                  <Col xs={6} className="text-end fs-10">
                    {new Date(orderData?.created).toLocaleString(
                      'en-IN',
                      TIME_OPTION,
                    )}
                  </Col>

                  <Col xs={6} className="text-muted fs-10">
                    Updated at
                  </Col>
                  <Col xs={6} className="text-end fs-10">
                    {new Date(orderData?.modified).toLocaleString(
                      'en-IN',
                      TIME_OPTION,
                    )}
                  </Col>
                </Row>
              </div>
            </Accordion.Body>
          </Accordion.Item>

          {/* Item Summary */}
          <Accordion.Item eventKey="1">
            <Accordion.Header>
              <span className="fw-semibold">Item Summary</span>
            </Accordion.Header>
            <Accordion.Body className="p-0">
              <div className="p-3">
                <Row className="g-2">
                  <Col xs={6} className="text-muted fs-10">
                    Total Items
                  </Col>
                  <Col xs={6} className="text-end fs-10">
                    {orderData?.order_summary?.total_items || 0}
                  </Col>

                  <Col xs={6} className="text-muted fs-10">
                    Delivered
                  </Col>
                  <Col xs={6} className="text-end fs-10">
                    {orderData?.order_summary?.delivered || 0}
                  </Col>

                  <Col xs={6} className="text-muted fs-10">
                    Dispatched
                  </Col>
                  <Col xs={6} className="text-end fs-10">
                    {orderData?.order_summary?.dispatched || 0}
                  </Col>

                  <Col xs={6} className="text-muted fs-10">
                    Cancelled
                  </Col>
                  <Col xs={6} className="text-end fs-10">
                    {orderData?.order_summary?.cancelled || 0}
                  </Col>
                </Row>
              </div>
            </Accordion.Body>
          </Accordion.Item>

          {/* Payment Summary */}
          <Accordion.Item eventKey="2">
            <Accordion.Header>
              <span className="fw-semibold">Payment Summary</span>
            </Accordion.Header>
            <Accordion.Body className="p-0">
              <div className="p-3">
                <Row className="g-2">
                  <Col xs={7} className="text-muted fs-10">
                    Payment Type
                  </Col>
                  <Col xs={5} className="text-end fs-10">
                    {orderData?.payment_type.toUpperCase()}
                  </Col>

                  <Col xs={7} className="text-muted fs-10">
                    Sub-Total before Discount & Taxes
                  </Col>
                  <Col xs={5} className="text-end fs-10">
                    ₹{orderData?.payment_total_net}
                  </Col>

                  <Col xs={7} className="text-muted fs-10">
                    Discount
                  </Col>
                  <Col xs={5} className="text-end fs-10 text-danger">
                    (-) ₹{orderData?.payment_total_discount}
                  </Col>

                  {parseFloat(orderData?.payment_total_shipping_charges) >
                    0 && (
                    <>
                      <Col xs={7} className="text-muted fs-10">
                        Shipping Charges
                      </Col>
                      <Col xs={5} className="text-end fs-10">
                        ₹{orderData?.payment_total_shipping_charges}
                      </Col>
                    </>
                  )}

                  {parseFloat(
                    orderData?.payment_total_cash_on_delivery_charges,
                  ) > 0 && (
                    <>
                      <Col xs={7} className="text-muted fs-10">
                        COD Charges
                      </Col>
                      <Col xs={5} className="text-end fs-10">
                        ₹{orderData?.payment_total_cash_on_delivery_charges}
                      </Col>
                    </>
                  )}

                  {parseFloat(orderData?.payment_total_tax) > 0 && (
                    <>
                      <Col xs={7} className="text-muted fs-10">
                        Total Tax on Sales
                      </Col>
                      <Col xs={5} className="text-end fs-10">
                        ₹{orderData?.payment_total_tax}
                      </Col>
                    </>
                  )}

                  <hr className="my-3" />

                  <Col xs={7} className="fw-semibold fs-10 mt-0">
                    Order Amount
                  </Col>
                  <Col xs={5} className="text-end fw-semibold fs-10 mt-0">
                    ₹{orderData?.payment_order_amount}
                  </Col>
                </Row>
              </div>
            </Accordion.Body>
          </Accordion.Item>

          {/* Billing/ Shipping Address */}
          <Accordion.Item eventKey="3">
            <Accordion.Header className="d-flex justify-content-between">
              <span className="fw-semibold">Billing/ Shipping Address</span>
            </Accordion.Header>
            <Accordion.Body className="p-0">
              <div className="p-3">
                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <h6 className="mb-0 fw-semibold">Billing Address</h6>
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0"
                      onClick={() => setIsEditingBilling(true)}
                    >
                      Edit
                    </Button>
                  </div>

                  <div className="text-muted mb-1 fw-semibold fs-10">Name</div>
                  <div className="fw-medium fs-10">
                    {orderData?.billing_address?.name}
                  </div>
                  <div className="text-muted mb-1 mt-2 fw-semibold fs-10">
                    Address
                  </div>
                  <div className="fw-medium fs-10">
                    {orderData?.billing_address?.address_line_1}
                    {orderData?.billing_address?.address_line_2 && (
                      <>, {orderData?.billing_address?.address_line_2}</>
                    )}
                    <br />
                    {orderData?.billing_address?.city},{' '}
                    {orderData?.billing_address?.state}
                    <br />
                    {orderData?.billing_address?.country}{' '}
                    {orderData?.billing_address?.pincode}
                  </div>
                </div>

                <div>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <h6 className="mb-0 fw-semibold">Shipping Address</h6>
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0"
                      onClick={() => setIsEditingShipping(true)}
                    >
                      Edit
                    </Button>
                  </div>

                  <div className="text-muted mb-1 fw-semibold fs-10">Name</div>
                  <div className="fw-medium fs-10">
                    {orderData?.shipping_address?.name}
                  </div>
                  <div className="text-muted mb-1 mt-2 fw-semibold fs-10">
                    Address
                  </div>
                  <div className="fw-medium fs-10">
                    {orderData?.shipping_address?.address_line_1}
                    {orderData?.shipping_address?.address_line_2 && (
                      <>, {orderData?.shipping_address?.address_line_2}</>
                    )}
                    <br />
                    {orderData?.shipping_address?.city},{' '}
                    {orderData?.shipping_address?.state}
                    <br />
                    {orderData?.shipping_address?.country}{' '}
                    {orderData?.shipping_address?.pincode}
                  </div>
                </div>
              </div>
            </Accordion.Body>
          </Accordion.Item>
        </Card>
      </Accordion>

      {/* Billing Address Edit Modal */}
      <AddressEditModal
        show={isEditingBilling}
        onHide={() => setIsEditingBilling(false)}
        onSuccess={refetch}
        type="billing"
        title="Edit Billing Address"
        orderId={orderId}
      />

      {/* Shipping Address Edit Modal */}
      <AddressEditModal
        show={isEditingShipping}
        onHide={() => setIsEditingShipping(false)}
        onSuccess={refetch}
        type="shipping"
        title="Edit Shipping Address"
        orderId={orderId}
      />
    </>
  );
};

export default OrderDetailSummary;
